import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import type { RootState } from "../../../store/store";
import {
  approveRosterChangeApi,
  DpRosterChangeRequest,
  DpRosterChangeStatus,
  listRosterChangeRequestsApi,
  rejectRosterChangeApi,
  ROSTER_CHANGE_STATUS_LABELS,
} from "../api/scheduleApi";
import { DP_PAGE, DP_ROSTERS_PAGE } from "../routes";
import { formatDateOnly } from "../utils/format";
import { useGoBack } from "../../../hooks/useGoBack";
import ReasonModal from "../components/ReasonModal";
import "./Dp.css";
import "./DpEco.css";

const BADGE: Record<DpRosterChangeStatus, string> = {
  PENDENTE: "dpBadge--orange",
  APROVADO: "dpBadge--green",
  REJEITADO: "dpBadge--red",
  CANCELADO: "dpBadge--gray",
};

const weekday = (value: string) => new Date(value).toLocaleDateString("pt-BR", { weekday: "short", timeZone: "UTC" });

/**
 * Pedidos de troca de folga feitos pelos colaboradores no portal. Aprovar troca os
 * dois dias na escala (e fixa); o sistema recusa a aprovação que deixaria mais de
 * 6 dias seguidos de trabalho ou uma semana sem descanso.
 */
export default function RosterChangeRequestsPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(DP_ROSTERS_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const [status, setStatus] = useState<DpRosterChangeStatus | "">("PENDENTE");
  const [items, setItems] = useState<DpRosterChangeRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<DpRosterChangeRequest | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      setItems(await listRosterChangeRequestsApi(token, { status: status || undefined }));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao listar os pedidos.");
    } finally {
      setLoading(false);
    }
  }, [token, status]);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (item: DpRosterChangeRequest, approve: boolean, note = "") => {
    if (!token) return;
    setBusyId(item.id);
    try {
      if (approve) await approveRosterChangeApi(token, item.id);
      else await rejectRosterChangeApi(token, item.id, note || undefined);
      toast.success(approve ? "Troca aprovada: a escala foi atualizada." : "Pedido recusado.");
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Não foi possível decidir o pedido.");
    } finally {
      setBusyId(null);
    }
  };

  if (!token) return null;

  return (
    <div className="dpLayout">
      <main className="dpMain">
        <div className="dpContainer">
          <EcoPageHeader
            crumbs={[
              { label: "Departamento Pessoal", onClick: () => navigate(DP_PAGE()) },
              { label: "Escala da loja", onClick: () => navigate(DP_ROSTERS_PAGE()) },
              { label: "Pedidos de troca" },
            ]}
            title="Pedidos de troca de folga"
            subtitle="O colaborador pede no portal para trabalhar num dia de folga e folgar num dia de trabalho. Aprovar troca os dois dias na escala."
            onBack={goBack}
            backLabel="Voltar para a escala"
          />

          <div className="dpControls">
            <div className="dpFormGroup" style={{ margin: 0, minWidth: 180 }}>
              <label htmlFor="changeStatus">Situação</label>
              <select id="changeStatus" className="dpSelect" value={status} onChange={(e) => setStatus(e.target.value as DpRosterChangeStatus | "")}>
                <option value="">Todas</option>
                {(Object.keys(ROSTER_CHANGE_STATUS_LABELS) as DpRosterChangeStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {ROSTER_CHANGE_STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="dpEmpty">Carregando...</div>
          ) : (
            <div className="dpTableWrapper">
              <table className="dpTable">
                <thead>
                  <tr>
                    <th>Colaborador</th>
                    <th>Loja / mês</th>
                    <th>Trabalhar em</th>
                    <th>Folgar em</th>
                    <th>Motivo</th>
                    <th>Situação</th>
                    <th style={{ width: 190 }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="dpEmpty">
                        Nenhum pedido.
                      </td>
                    </tr>
                  ) : (
                    items.map((item) => (
                      <tr key={item.id}>
                        <td>{item.employee.socialName || item.employee.fullName}</td>
                        <td>
                          {item.roster.store.nomeFantasia} · {String(item.roster.month).padStart(2, "0")}/{item.roster.year}
                        </td>
                        <td>
                          {formatDateOnly(item.fromDate)} ({weekday(item.fromDate)})
                        </td>
                        <td>
                          {formatDateOnly(item.toDate)} ({weekday(item.toDate)})
                        </td>
                        <td>{item.reason || "—"}</td>
                        <td>
                          <span className={`dpBadge ${BADGE[item.status]}`}>{ROSTER_CHANGE_STATUS_LABELS[item.status]}</span>
                          {item.decisionNote ? <div className="dpFormHint">{item.decisionNote}</div> : null}
                        </td>
                        <td>
                          {item.status === "PENDENTE" ? (
                            <div className="dpTable__actions">
                              <button className="dpBtn dpBtn--primary dpBtn--small" disabled={busyId === item.id} onClick={() => decide(item, true)}>
                                Aprovar
                              </button>
                              <button className="dpBtn dpBtn--danger dpBtn--small" disabled={busyId === item.id} onClick={() => setRejecting(item)}>
                                Recusar
                              </button>
                            </div>
                          ) : (
                            <span className="dpMuted">{item.decidedBy?.nomeCompleto ?? ""}</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {rejecting ? (
        <ReasonModal
          title="Recusar o pedido de troca"
          description={`O colaborador vê o motivo no portal (${rejecting.employee.socialName || rejecting.employee.fullName}).`}
          confirmLabel="Recusar pedido"
          onClose={() => setRejecting(null)}
          onConfirm={async (reason) => {
            const target = rejecting;
            setRejecting(null);
            await decide(target, false, reason);
          }}
        />
      ) : null}
    </div>
  );
}
